import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { calendarApi, calendarsMgmtApi, remoteApi } from '../utils/api';
import {
  ChevronLeft, ChevronRight, Loader, X, MapPin, User, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday,
  parseISO, isWithinInterval
} from 'date-fns';
import { es } from 'date-fns/locale';

const TYPE_COLORS = {
  vacation_approved: { bg: '#00a651', text: '#fff', label: 'Vacaciones' },
  vacation_pending:  { bg: '#eab308', text: '#fff', label: 'Vacaciones (pend.)' },
  vacation_cancellation_requested: { bg: '#f97316', text: '#fff', label: 'Cancelación vacaciones' },
  remote_approved:   { bg: '#0ea5e9', text: '#fff', label: 'Mi teletrabajo' },
  holiday:           { bg: '#dc2626', text: '#fff', label: 'Festivo' },
  team_remote:       { bg: '#7c3aed', text: '#fff', label: 'Teletrabajo compañero' },
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-xs">
    {Object.entries(TYPE_COLORS).map(([key, val]) => (
      <div key={key} className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: val.bg }} />
        <span className="text-muted-foreground">{val.label}</span>
      </div>
    ))}
    <div className="flex items-center gap-1.5 pl-3 border-l border-border/60">
      <span className="text-[9px] font-bold px-1 rounded-sm bg-slate-400/25 text-slate-700 dark:text-slate-200">M</span>
      <span className="text-muted-foreground">Mañana</span>
      <span className="text-[9px] font-bold px-1 rounded-sm bg-slate-400/25 text-slate-700 dark:text-slate-200 ml-1">T</span>
      <span className="text-muted-foreground">Tarde</span>
    </div>
  </div>
);

function getEventTypeKey(ev) {
  const base = ev.type === 'vacation' ? 'vacation' : 'remote';
  const status = ev.status === 'cancellation_requested' ? 'cancellation_requested' : ev.status;
  return `${base}_${status}`;
}

function EventPopover({ event, onClose }) {
  if (!event) return null;
  const typeKey = getEventTypeKey(event);
  const color = TYPE_COLORS[typeKey] || { bg: '#64748b', text: '#fff' };
  const periodLabels = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl p-5 max-w-sm w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: color.bg }} />
            <span className="font-semibold text-sm font-heading">
              {event.type === 'vacation' ? 'Vacaciones' : 'Teletrabajo'}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            <span className="font-medium text-foreground">{event.user_name}</span>
          </div>
          <div className="text-muted-foreground">
            {event.start_date === event.end_date
              ? event.start_date
              : `${event.start_date} → ${event.end_date}`
            }
            {event.working_days && ` (${event.working_days} días laborables)`}
          </div>
          {event.period_type && (
            <div className="text-muted-foreground">
              Tramo: <span className="font-medium text-foreground">{periodLabels[event.period_type] || event.period_type}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span>{event.location}</span>
            </div>
          )}
          <div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              event.status === 'approved' ? 'status-approved' :
              event.status === 'pending' ? 'status-pending' :
              event.status === 'rejected' ? 'status-rejected' : 'status-cancelled'
            }`}>
            {event.status === 'approved' ? 'Aprobada' :
             event.status === 'pending' ? 'Pendiente' :
             event.status === 'rejected' ? 'Rechazada' :
             event.status === 'cancellation_requested' ? 'Cancelación solicitada' : 'Cancelada'}
            </span>
          </div>
          {event.notes && <div className="text-muted-foreground text-xs italic">{event.notes}</div>}
        </div>
      </div>
    </div>
  );
}

function DayDetailModal({ day, events, holiday, onClose, onSelectEvent }) {
  if (!day) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl p-5 max-w-sm w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <span className="font-semibold text-sm font-heading capitalize">
            {format(day, "d 'de' MMMM", { locale: es })}
          </span>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors" data-testid="day-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {holiday && (
          <div className="flex items-center gap-1.5 text-xs font-medium px-2 py-1.5 rounded-lg mb-2" style={{ background: '#dc2626', color: '#fff' }}>
            <Star className="h-3 w-3" /> {holiday.name}
          </div>
        )}
        <div className="space-y-1.5" data-testid="day-detail-list">
          {events.length === 0 && <p className="text-xs text-muted-foreground">Sin eventos este día.</p>}
          {events.map((ev) => {
            const isTeamOnly = !ev.status;
            const typeKey = getEventTypeKey(ev);
            const colorInfo = isTeamOnly ? { bg: '#7c3aed', text: '#fff' } : (TYPE_COLORS[typeKey] || { bg: '#64748b', text: '#fff' });
            const isRemote = ev.type === 'remote_work';
            const periodLabels = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };
            return (
              <button
                key={ev.id}
                onClick={() => (isTeamOnly ? null : onSelectEvent(ev))}
                className={`w-full text-left text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition-opacity hover:opacity-85 ${isTeamOnly ? 'cursor-default' : ''}`}
                style={{ background: colorInfo.bg, color: colorInfo.text }}
              >
                <span className="h-2 w-2 rounded-full bg-white/70 flex-shrink-0" />
                <span className="flex-1 truncate">
                  {ev.user_name} — {ev.type === 'vacation' ? 'Vacaciones' : 'Teletrabajo'}
                  {isRemote && ev.period_type && ` (${periodLabels[ev.period_type] || ev.period_type})`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CorporateCalendar() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [teamEvents, setTeamEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [view, setView] = useState('month');

  const locale = lang === 'es' ? es : undefined;
  const calendarCode = user?.calendar_code || 'Condesa5';

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      const eventsRes = await calendarApi.events({ start_date: start, end_date: end });
      setEvents(eventsRes.data);
      // Team colleagues telework (non-blocking)
      try {
        const teamRes = await remoteApi.teamCalendar(currentDate.getFullYear(), currentDate.getMonth() + 1);
        setTeamEvents(teamRes.data || []);
      } catch { setTeamEvents([]); }
      // Holidays: non-blocking, fail silently
      try {
        const holidaysRes = await calendarsMgmtApi.getHolidays(calendarCode, year);
        setHolidays(holidaysRes.data || []);
      } catch { setHolidays([]); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentDate, calendarCode]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const getEventsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return events.filter(ev => {
      if (ev.start_date === ev.end_date) return ev.start_date === dayStr;
      try {
        return isWithinInterval(day, { start: parseISO(ev.start_date), end: parseISO(ev.end_date) });
      } catch { return false; }
    });
  };

  const getHolidayForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return holidays.find(h => h.date === dayStr) || null;
  };

  const getTeamEventsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    // Build a set of (user_id, date) already shown via the main events feed
    // so we never paint the same teletrabajo twice (once blue / once purple).
    const seen = new Set();
    for (const ev of events) {
      if (ev.type !== 'remote_work') continue;
      try {
        const start = parseISO(ev.start_date);
        const end = parseISO(ev.end_date || ev.start_date);
        const inRange =
          ev.start_date === ev.end_date
            ? ev.start_date === dayStr
            : isWithinInterval(day, { start, end });
        if (inRange) seen.add(`${ev.user_id}|${dayStr}`);
      } catch { /* ignore */ }
    }
    return teamEvents.filter(
      (e) => e.date === dayStr && !seen.has(`${e.user_id}|${dayStr}`)
    );
  };

  // Period label
  const periodLabel = (pt) => pt === 'morning' ? 'Mañana' : pt === 'afternoon' ? 'Tarde' : 'Día completo';
  // Compact indicator (fits inside the small day cells)
  const periodBadge = (pt) => pt === 'morning' ? 'M' : pt === 'afternoon' ? 'T' : null;

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks = [];
  let day = calStart;
  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  return (
    <div className="space-y-4 max-w-6xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading">Calendario Corporativo</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Vista de ausencias y teletrabajo del equipo</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(subMonths(currentDate, 1))} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} data-testid="cal-today" className="min-w-16">
            Hoy
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(addMonths(currentDate, 1))} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Month title */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-bold font-heading capitalize">
            {format(currentDate, 'MMMM yyyy', { locale })}
          </h2>
          {loading && <Loader className="h-4 w-4 animate-spin text-brand" />}
        </div>

        {/* Legend */}
        <div className="px-5 py-2.5 border-b border-border bg-muted/30">
          <Legend />
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {dayNames.map((name, i) => (
            <div key={name} className={`text-center text-xs font-semibold py-2.5 text-muted-foreground ${i >= 5 ? 'bg-muted/30' : ''}`}>
              {name}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="divide-y divide-border">
          {weeks.map((week, wi) => (
            <div key={week[0].toISOString()} className="grid grid-cols-7 divide-x divide-border" style={{ minHeight: '100px' }}>
              {week.map((d, di) => {
                const isCurrentMonth = isSameMonth(d, currentDate);
                const isToday_ = isToday(d);
                const dayEvents = getEventsForDay(d);
                const dayTeamEvents = getTeamEventsForDay(d);
                const allDayEvents = [...dayEvents, ...dayTeamEvents];
                const holiday = getHolidayForDay(d);
                const isWeekend = di >= 5;
                const maxVisible = holiday ? 2 : 3;
                const visibleEvents = allDayEvents.slice(0, maxVisible);
                const hiddenCount = allDayEvents.length - visibleEvents.length;

                return (
                  <div
                    key={d.toISOString()}
                    className={`p-1.5 ${holiday ? 'bg-red-50 dark:bg-red-950/20' : !isCurrentMonth ? 'bg-muted/20' : isWeekend ? 'bg-muted/10' : 'bg-card'} ${isToday_ ? 'ring-inset ring-1 ring-brand' : ''}`}
                  >
                    <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                      isToday_ ? 'bg-brand text-white' :
                      !isCurrentMonth ? 'text-muted-foreground/40' :
                      isWeekend ? 'text-muted-foreground/60' : 'text-foreground'
                    }`}>
                      {format(d, 'd')}
                    </div>
                    <div className="space-y-0.5">
                      {/* Festivo */}
                      {holiday && isCurrentMonth && (
                        <button
                          onClick={() => setSelectedHoliday(holiday)}
                          className="w-full text-left text-xs px-1.5 py-0.5 rounded truncate font-medium transition-opacity hover:opacity-80 flex items-center gap-1"
                          style={{ background: '#dc2626', color: '#fff' }}
                          title={holiday.name}
                        >
                          <Star className="h-2.5 w-2.5 flex-shrink-0" />
                          <span className="truncate">{holiday.name}</span>
                        </button>
                      )}
                      {/* Eventos del día (propios + equipo), sin límite artificial de fuente */}
                      {visibleEvents.map((ev) => {
                        const isTeamOnly = !ev.status; // team_calendar entries have no `status`/color
                        const typeKey = getEventTypeKey(ev);
                        const colorInfo = isTeamOnly
                          ? { bg: '#7c3aed', text: '#fff' }
                          : (TYPE_COLORS[typeKey] || { bg: '#64748b', text: '#fff' });
                        const isRemote = ev.type === 'remote_work';
                        const pb = isRemote ? periodBadge(ev.period_type) : null;
                        const evTitle = isRemote
                          ? `${ev.user_name} · ${periodLabel(ev.period_type)}`
                          : ev.title;
                        return (
                          <button
                            key={ev.id}
                            onClick={() => (isTeamOnly ? setSelectedDay({ date: d, events: allDayEvents, holiday }) : setSelectedEvent(ev))}
                            className={`w-full text-left text-xs px-1.5 py-0.5 rounded truncate font-medium transition-opacity hover:opacity-80 flex items-center gap-1 ${isTeamOnly ? 'opacity-75' : ''}`}
                            style={{ background: colorInfo.bg, color: colorInfo.text }}
                            title={evTitle}
                          >
                            <span className="truncate flex-1 min-w-0">{ev.user_name?.split(' ')[0]}</span>
                            {pb && (
                              <span
                                className="text-[9px] font-bold px-1 rounded-sm flex-shrink-0"
                                style={{ background: 'rgba(255,255,255,0.25)' }}
                                aria-label={periodLabel(ev.period_type)}
                              >
                                {pb}
                              </span>
                            )}
                          </button>
                        );
                      })}
                      {hiddenCount > 0 && (
                        <button
                          onClick={() => setSelectedDay({ date: d, events: allDayEvents, holiday })}
                          data-testid={`cal-more-${format(d, 'yyyy-MM-dd')}`}
                          className="w-full text-left text-xs text-brand hover:underline px-1 font-medium"
                        >
                          +{hiddenCount} más
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedEvent && <EventPopover event={selectedEvent} onClose={() => setSelectedEvent(null)} />}

      {selectedDay && (
        <DayDetailModal
          day={selectedDay.date}
          events={selectedDay.events}
          holiday={selectedDay.holiday}
          onClose={() => setSelectedDay(null)}
          onSelectEvent={(ev) => { setSelectedDay(null); setSelectedEvent(ev); }}
        />
      )}

      {/* Holiday popover */}
      {selectedHoliday && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedHoliday(null)}>
          <div className="bg-card border border-border rounded-xl shadow-xl p-5 max-w-xs w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-red-600" />
                <span className="font-semibold text-sm">Festivo</span>
              </div>
              <button onClick={() => setSelectedHoliday(null)} className="p-1 rounded hover:bg-muted transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm font-medium text-foreground mb-1">{selectedHoliday.name}</p>
            <p className="text-xs text-muted-foreground">{selectedHoliday.date}</p>
            <p className="text-xs text-muted-foreground mt-1">Calendario: <span className="font-medium">{calendarCode}</span></p>
          </div>
        </div>
      )}
    </div>
  );
}
